// File: frontend/src/components/layout/Footer.js

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { Facebook, Twitter, Linkedin, Instagram, Globe, ArrowRight } from 'lucide-react';
import apiService from '../../services/apiService';
import { getCurrentUserContext } from '../../utils/bootstrap';
import { API_CONFIG } from '../../config/api';
import { INSURANCE_PRODUCTS } from '../../config/insuranceProducts';
import AuthModal from '../auth/AuthModal';
import TextInput from '../common/TextInput';
import EmailInput from '../common/EmailInput';
import styles from './Footer.module.css';
import { ASSETS_BASE_URL } from '../../config/constants';
import { Turnstile } from '@marsidev/react-turnstile';

const LANGUAGES = {
  'en-us': { name: 'English (US)', flag: '🇺🇸' },
  'en-ca': { name: 'English (CA)', flag: '🇨🇦' },
  'es-mx': { name: 'Español (MX)', flag: '🇲🇽' },
  'fr-ca': { name: 'Français (CA)', flag: '🇨🇦' }
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { language, changeLanguage, t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const dropdownRef = useRef(null);

  // Newsletter state
  const [nlForm, setNlForm] = useState({ first_name: '', last_name: '', email_address: '' });
  const [nlErrors, setNlErrors] = useState({});
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [nlSubscribed, setNlSubscribed] = useState(false);
  const [nlSuccessMsg, setNlSuccessMsg] = useState('');

  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsLanguageOpen(false);
  };

  const handleProductClick = (slug) => {
    navigate(`/products/${slug}`);
  };

  const handleWalletClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard/policies');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleCookiePreferences = () => {
    localStorage.removeItem('cookie_consent');
    window.location.reload();
  };

  // Newsletter handlers
  const handleNlInputChange = (name, value) => {
    setNlForm(prev => ({ ...prev, [name]: value }));
    if (nlErrors[name]) setNlErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleNlEmailChange = (value) => {
    setNlForm(prev => ({ ...prev, email_address: value }));
    if (nlErrors.email_address) setNlErrors(prev => ({ ...prev, email_address: null }));
  };

  const validateNl = () => {
    const errs = {};
    if (!nlForm.first_name.trim()) errs.first_name = t('footer.nl.error_first_name', 'First name is required');
    if (!nlForm.last_name.trim()) errs.last_name = t('footer.nl.error_last_name', 'Last name is required');
    if (!nlForm.email_address.trim()) {
      errs.email_address = t('footer.nl.error_email_required', 'Email is required');
    } else if (!/\S+@\S+\.\S+/.test(nlForm.email_address)) {
      errs.email_address = t('footer.nl.error_email_invalid', 'Please enter a valid email');
    }
    return errs;
  };

  const handleNlSubmit = async (e) => {
    e.preventDefault();
    
    if (!turnstileToken) {
      setNlErrors({ submit: 'Security check not complete. Please wait a moment and try again.' });
      return;
    }

    const validationErrors = validateNl();
    if (Object.keys(validationErrors).length > 0) {
      setNlErrors(validationErrors);
      return;
    }
    setNlSubmitting(true);
    setNlErrors({});
    try {
      const userContext = getCurrentUserContext();
      const response = await apiService.subscribeNewsletter({
        user_id: userContext?.userId,
        enterprise_id: API_CONFIG.enterpriseId,
        first_name: nlForm.first_name.trim(),
        last_name: nlForm.last_name.trim(),
        email_address: nlForm.email_address.trim().toLowerCase(),
        language: navigator.language.toLowerCase() || 'en-ca',
        user_agent: navigator.userAgent,
        turnstile_token: turnstileToken,
      });
      if (response.success) {
        setNlSubscribed(true);
        setNlSuccessMsg(response.message || t('footer.nl.success', 'Thanks for subscribing! Check your email.'));
        setTurnstileToken(null);
        setNlForm({ first_name: '', last_name: '', email_address: '' });
      } else {
        setNlErrors({ submit: response.error || t('footer.nl.error_failed', 'Subscription failed. Please try again.') });
        turnstileRef.current?.reset();
      }
    } catch (error) {
      setNlErrors({ submit: t('footer.nl.error_generic', 'Something went wrong. Please try again.') });
      turnstileRef.current?.reset();
    } finally {
      setNlSubmitting(false);
    }
  };

  // Get all insurance products
  const insuranceProducts = Object.values(INSURANCE_PRODUCTS);

  return (
    <>
      <footer className={styles.footer}>
        {/* Newsletter banner */}
        <div className={styles.newsletterBanner}>
          <div className={styles.newsletterContainer}>
            <div className={styles.newsletterText}>
              <h3 className={styles.newsletterTitle}>
                {t('footer.nl.title', 'Stay ahead of your insurance')}
              </h3>
              <p className={styles.newsletterSubtitle}>
                {t('footer.nl.subtitle', 'Weekly tips, rate alerts, and coverage insights — straight to your inbox.')}
              </p>
            </div>

            <div className={styles.newsletterFormWrapper}>
              {!nlSubscribed ? (
                <form onSubmit={handleNlSubmit} className={styles.newsletterForm}>
                  <div className={styles.nlRow}>
                    <div className={styles.nlField}>
                      <TextInput
                        name="first_name"
                        value={nlForm.first_name}
                        onChange={handleNlInputChange}
                        placeholder={t('footer.nl.first_name', 'First name')}
                        error={nlErrors.first_name}
                        disabled={nlSubmitting}
                      />
                    </div>
                    <div className={styles.nlField}>
                      <TextInput
                        name="last_name"
                        value={nlForm.last_name}
                        onChange={handleNlInputChange}
                        placeholder={t('footer.nl.last_name', 'Last name')}
                        error={nlErrors.last_name}
                        disabled={nlSubmitting}
                      />
                    </div>
                  </div>
                  <div className={styles.nlEmailRow}>
                    <div className={styles.nlEmailField}>
                      <EmailInput
                        value={nlForm.email_address}
                        onChange={handleNlEmailChange}
                        placeholder={t('footer.nl.email', 'Enter your email')}
                        error={nlErrors.email_address}
                        disabled={nlSubmitting}
                      />
                    </div>

                    <Turnstile
                      ref={turnstileRef}
                      siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                      options={{ theme: 'auto' }}
                    />

                    <button
                      type="submit"
                      className={styles.nlSubmitButton}
                      disabled={nlSubmitting}
                    >
                      {nlSubmitting ? (
                        t('footer.nl.subscribing', 'Subscribing...')
                      ) : (
                        <>
                          <span>{t('footer.nl.subscribe', 'Subscribe')}</span>
                          <ArrowRight size={16} strokeWidth={2.5} />
                        </>
                      )}
                    </button>
                  </div>
                  {nlErrors.submit && (
                    <p className={styles.nlError}>{nlErrors.submit}</p>
                  )}
                  <p className={styles.nlDisclaimer}>
                    {t('footer.nl.disclaimer', 'No spam. Unsubscribe anytime.')}
                  </p>
                </form>
              ) : (
                <div className={styles.nlSuccess}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="currentColor" />
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{nlSuccessMsg}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main footer */}
        <div className={styles.main}>
          <div className={styles.container}>
            <div className={styles.grid}>
              {/* Brand */}
              <div className={styles.brandColumn}>
                <img
                  src={`${ASSETS_BASE_URL}/logos/allegory-logo-blue.svg`}
                  alt="Allegory Insurance"
                  className={styles.logo}
                />
                <p className={styles.tagline}>
                  {t('footer.tagline', 'Your Smart Digital Insurance Wallet')}
                </p>

                <div className={styles.social}>
                  <a href="https://www.facebook.com/Allegory-Insurance-115129876917282" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className={styles.socialLink}>
                    <Facebook size={18} strokeWidth={2} />
                  </a>
                  <a href="https://x.com/allegory_app" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className={styles.socialLink}>
                    <Twitter size={18} strokeWidth={2} />
                  </a>
                  <a href="https://www.linkedin.com/company/allegoryapp" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className={styles.socialLink}>
                    <Linkedin size={18} strokeWidth={2} />
                  </a>
                  <a href="https://www.instagram.com/allegory_app/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={styles.socialLink}>
                    <Instagram size={18} strokeWidth={2} />
                  </a>
                </div>
              </div>

              {/* Products */}
              <div className={styles.column}>
                <h4 className={styles.columnTitle}>
                  {t('footer.products_heading', 'Products')}
                </h4>
                <div className={styles.columnLinks}>
                  <button onClick={handleWalletClick} className={styles.link}>
                    {t('footer.wallet_link', 'Wallet')}
                  </button>
                  <Link to="/products" className={styles.link}>
                    {t('footer.all_products_link', 'All Products')}
                  </Link>
                  {/* <Link to="/pricing" className={styles.link}>
                    {t('footer.pricing_link', 'Pricing')}
                  </Link> */}
                  <Link to="/#how-it-works" className={styles.link}>
                    {t('footer.how_it_works_link', 'How It Works')}
                  </Link>
                  <Link to="/#features" className={styles.link}>
                    {t('footer.features_link', 'Features')}
                  </Link>
                </div>
              </div>

              {/* Insurance Types */}
              <div className={styles.column}>
                <h4 className={styles.columnTitle}>
                  {t('footer.insurance_heading', 'Insurance Types')}
                </h4>
                <div className={styles.columnLinks}>
                  {insuranceProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductClick(product.slug)}
                      className={styles.link}
                    >
                      {product.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Company */}
              <div className={styles.column}>
                <h4 className={styles.columnTitle}>{t('footer.company_heading', 'Company')}</h4>
                <div className={styles.columnLinks}>
                  <Link to="/about" className={styles.link}>{t('footer.about_link', 'About')}</Link>
                  <a href="https://allegory.app" target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {t('footer.allegoryos_link', 'AllegoryOS')}
                  </a>
                  <a href="https://www.linkedin.com/company/allegoryapp" target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {t('footer.join_us_link', 'Join Us')}
                  </a>
                  <Link to="/contact" className={styles.link}>{t('footer.contact_link', 'Contact')}</Link>
                </div>
              </div>

              {/* Legal */}
              <div className={styles.column}>
                <h4 className={styles.columnTitle}>{t('footer.legal_heading', 'Legal')}</h4>
                <div className={styles.columnLinks}>
                  <Link to="/privacy-policy" className={styles.link}>{t('footer.privacy_link', 'Privacy Policy')}</Link>
                  <Link to="/terms-of-service" className={styles.link}>{t('footer.terms_link', 'Terms of Service')}</Link>
                  <Link to="/sms-terms" className={styles.link}>{t('footer.sms_terms_link', 'SMS Terms')}</Link>
                  <button onClick={handleCookiePreferences} className={styles.link}>
                    {t('footer.cookie_preferences_link', 'Cookie Preferences')}
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom bar */}
            <div className={styles.bottom}>
              <div className={styles.bottomLeft}>
                <p className={styles.copyright}>
                  © {currentYear} {t('footer.copyright', 'Allegory Technology Inc. All rights reserved.')}
                </p>

                <div className={styles.languagePicker} ref={dropdownRef}>
                  <button
                    className={styles.languageButton}
                    onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                    aria-label="Select language"
                  >
                    <Globe size={14} strokeWidth={2} />
                    <span>{LANGUAGES[language]?.name || 'Language'}</span>
                  </button>

                  {isLanguageOpen && (
                    <div className={styles.languageDropdown}>
                      {Object.entries(LANGUAGES).map(([code, { name, flag }]) => (
                        <button
                          key={code}
                          className={`${styles.languageOption} ${language === code ? styles.languageOptionActive : ''}`}
                          onClick={() => handleLanguageChange(code)}
                        >
                          <span>{flag}</span>
                          <span>{name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType="sign_up"
      />
    </>
  );
};

export default Footer;